import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const API_BASE    = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STOREFRONT_API_URL) || 'https://dashboard.offloadhost.com';
const CUSTOMER_ID = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CUSTOMER_ID) || '';

// Bearer-token-authenticated POST to the storefront auth API. The session
// token is set by AuthContext on login under `userSession.token`.
function getToken() {
  try {
    const raw = localStorage.getItem('userSession');
    if (!raw) return null;
    return JSON.parse(raw)?.token || null;
  } catch (_) { return null; }
}
async function shopperApi(path, init = {}) {
  const token = getToken();
  const headers = { 'Accept': 'application/json', ...(init.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (init.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const r = await fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/auth${path}`, { ...init, headers });
  let data = null;
  try { data = await r.json(); } catch (_) {}
  return { ok: r.ok, status: r.status, data };
}

// Step 3: Stripe Elements payment confirmation. Lives INSIDE <Elements> so
// useStripe/useElements have a Provider in scope.
function PayInner({ total, onSuccess, onError, busy, setBusy, orderRef }) {
  const stripe   = useStripe();
  const elements = useElements();

  async function pay() {
    if (!stripe || !elements) {
      onError('Payment is still loading. Please try again.');
      return;
    }
    setBusy(true);
    onError(null);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + (orderRef ? `/user-account-dashboard?paid=${encodeURIComponent(orderRef)}` : '/user-account-dashboard'),
      },
      redirect: 'if_required',
    });
    if (error) {
      setBusy(false);
      onError(error.message || 'Payment failed. Please check your card and try again.');
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onSuccess(orderRef || '');
      return;
    }
    setBusy(false);
    onError('Payment did not complete. Please try again.');
  }

  return (
    <div className="space-y-4">
      <PaymentElement />
      <Button
        onClick={pay}
        disabled={busy || !stripe || !elements}
        iconName="Lock"
        iconPosition="left"
        className="w-full py-4 text-base"
      >
        {busy ? 'Confirming…' : `Place Order · $${total.toFixed(2)}`}
      </Button>
    </div>
  );
}

const CheckoutFlow = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const { user } = useAuth();

  // Auth gate — checkout requires sign-in (server uses the bearer to claim ownership).
  useEffect(() => {
    if (!user?.email) navigate('/user-login?next=/checkout');
  }, [user]);

  // Cart-empty redirect
  useEffect(() => {
    if (cart.items.length === 0) navigate('/shopping-cart');
  }, [cart.items.length]);

  const cartItems = cart.items;
  const subtotal = cartItems.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0);
  const shipping = subtotal >= 50 ? 0 : (subtotal > 0 ? 5.99 : 0);
  const serverTaxRef = useRef(0);
  const tax = serverTaxRef.current / 100;
  const total = subtotal + shipping + tax;

  const [step, setStep] = useState(1);                 // 1 = address, 2 = payment
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret,  setClientSecret]  = useState(null);
  const [orderRef,      setOrderRef]      = useState(null);
  const [orderPlaced,   setOrderPlaced]   = useState(false);
  const [placeErr,      setPlaceErr]      = useState(null);
  const [creating,      setCreating]      = useState(false);
  const [busy,          setBusy]          = useState(false);

  // Idempotency key for the place-order POST (per-cart). Reset when items
  // change so a different cart gets a different key.
  const idemKeyRef = useRef('');
  if (!idemKeyRef.current) {
    idemKeyRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : (Date.now() + '-' + Math.random().toString(36).slice(2));
  }

  // Address form — single inline form; address-book CRUD UX is a future addition.
  const split = (user?.name || '').trim().split(' ');
  const [addr, setAddr] = useState({
    first_name:  split[0] || '',
    last_name:   split.slice(1).join(' ') || '',
    address_1:   '',
    address_2:   '',
    city:        '',
    state:       '',
    postal_code: '',
    country:     'US',
    phone:       '',
  });
  const onAddr = (key) => (e) => setAddr((a) => ({ ...a, [key]: e.target.value }));

  function addressOk() {
    return addr.first_name && addr.last_name && addr.address_1
        && addr.city && addr.state && addr.postal_code && addr.country;
  }

  // Step 1 → Step 2: create the order on the server, get back client_secret + publishable_key
  async function proceedToPayment() {
    if (!addressOk()) { setPlaceErr('Please fill in all required address fields.'); return; }
    setCreating(true);
    setPlaceErr(null);

    const payload = {
      items: cartItems.map((it) => ({ product_id: it.id, qty: it.quantity || 1 })),
      billing:  addr,
      shipping: addr,
      payment_method: 'card',
    };
    const { ok, status, data } = await shopperApi('/orders/create', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'X-Idempotency-Key': idemKeyRef.current },
    });
    setCreating(false);
    if (!ok || !data) {
      setPlaceErr(data?.error || (status === 401 ? 'Please sign in to continue.' : 'Could not start checkout. Please try again.'));
      if (status === 401) navigate('/user-login?next=/checkout');
      return;
    }
    if (!data.client_secret || !data.publishable_key) {
      setPlaceErr('Payment isn\'t configured for this store yet. The merchant needs to add their Stripe keys.');
      return;
    }
    setOrderRef(data.reference || '');
    setClientSecret(data.client_secret);
    setStripePromise(loadStripe(data.publishable_key));
    if (typeof data.tax_cents === 'number') serverTaxRef.current = data.tax_cents;
    setStep(2);
  }

  function handlePaySuccess(ref) {
    cart.clearCart();
    setOrderRef(ref);
    setOrderPlaced(true);
  }

  if (orderPlaced) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={48} className="text-primary" />
        </div>
        <h1 className="text-3xl font-heading font-semibold mb-2">Order Confirmed</h1>
        <p className="text-muted-foreground mb-6">
          Your order <strong>{orderRef}</strong> has been placed. We'll email you when it ships.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate('/user-account-dashboard', { state: { section: 'orders' } })}>Track Your Order</Button>
          <Button variant="outline" onClick={() => navigate('/product-catalog')}>Continue Shopping</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-heading font-bold mb-2">Checkout</h1>
      <div className="flex items-center gap-3 mb-8 text-sm text-muted-foreground">
        <span className={step >= 1 ? 'text-primary font-semibold' : ''}>1. Address</span>
        <Icon name="ChevronRight" size={14} />
        <span className={step >= 2 ? 'text-primary font-semibold' : ''}>2. Payment</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: step content */}
        <div className="lg:col-span-2 space-y-6">
          {step === 1 && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-xl font-semibold">Shipping Address</h2>
              <div className="grid grid-cols-2 gap-3">
                <Input label="First name" value={addr.first_name} onChange={onAddr('first_name')} required />
                <Input label="Last name"  value={addr.last_name}  onChange={onAddr('last_name')}  required />
              </div>
              <Input label="Street address" value={addr.address_1} onChange={onAddr('address_1')} required />
              <Input label="Apt / suite (optional)" value={addr.address_2} onChange={onAddr('address_2')} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="City"  value={addr.city}  onChange={onAddr('city')}  required />
                <Input label="State / region" value={addr.state} onChange={onAddr('state')} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="ZIP / postal code" value={addr.postal_code} onChange={onAddr('postal_code')} required />
                <Input label="Country (2-letter)" value={addr.country} onChange={onAddr('country')} maxLength={2} required />
              </div>
              <Input label="Phone (optional)" value={addr.phone} onChange={onAddr('phone')} />
              {placeErr && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{placeErr}</div>
              )}
              <Button onClick={proceedToPayment} disabled={creating} className="w-full">
                {creating ? 'Preparing payment…' : 'Continue to Payment'}
              </Button>
            </div>
          )}

          {step === 2 && clientSecret && stripePromise && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Payment</h2>
                <button onClick={() => setStep(1)} className="text-xs text-primary hover:underline">Edit address</button>
              </div>
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <PayInner
                  total={total}
                  busy={busy}
                  setBusy={setBusy}
                  onSuccess={handlePaySuccess}
                  onError={(msg) => setPlaceErr(msg)}
                  orderRef={orderRef}
                />
              </Elements>
              {placeErr && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{placeErr}</div>
              )}
            </div>
          )}
        </div>

        {/* Right: order summary */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 sticky top-24">
            <h2 className="text-xl font-semibold">Order Summary</h2>
            <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {cartItems.map((it) => (
                <li key={it.id} className="flex gap-3 items-start">
                  <div className="w-12 h-12 bg-muted rounded-md overflow-hidden flex-shrink-0">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{it.name}</p>
                    <p className="text-xs text-muted-foreground">Qty {it.quantity}</p>
                  </div>
                  <p className="text-sm font-medium">${((it.price || 0) * (it.quantity || 1)).toFixed(2)}</p>
                </li>
              ))}
            </ul>
            <div className="space-y-1 text-sm pt-4 border-t border-border">
              <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span></div>
              {tax > 0 && <div className="flex justify-between"><span>Tax</span><span>${tax.toFixed(2)}</span></div>}
              {step === 1 && tax === 0 && (
                <div className="flex justify-between text-muted-foreground text-xs"><span>Tax</span><span>Calculated at payment</span></div>
              )}
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
            <Link to="/shopping-cart" className="text-xs text-primary hover:underline block text-center">
              ← Back to cart
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutFlow;
